import type { ColDef } from "ag-grid-community";

import type {
  ColumnDef as EntityColumnDef,
  FormatterId,
} from "@blis/insights/entities/types";

import { ExploreAccessionCell } from "../components/ExploreAccessionCell";
import { formatExploreCellValue } from "./formatValue";

export function buildExploreColumnDefs(
  columns: readonly EntityColumnDef[],
  defaultDateField: string,
  options?: { enableColumnFilters?: boolean },
): ColDef[] {
  const enableColumnFilters = options?.enableColumnFilters ?? false;
  return columns
    .filter((c) => !c.phi)
    .map((c) => {
      const cellFormatter = c.formatter;
      return {
        field: c.field,
        headerName: c.label,
        hide: !c.defaultVisible,
        ...(enableColumnFilters
          ? {
              filter: columnFilterType(c.type),
              filterParams: columnFilterParams(c),
            }
          : { filter: false }),
        sortable: true,
        resizable: true,
        ...(c.field === "accession_id"
          ? {
              minWidth: 120,
              cellRenderer: ExploreAccessionCell,
            }
          : c.field === "client_org" ||
              c.field === "receiving_org" ||
              c.field === "final_result_display" ||
              c.field === "initial_result_display"
            ? { minWidth: 200 }
            : undefined),
        ...(c.type === "boolean"
          ? {
              valueFormatter: (params: { value: unknown }) => {
                if (params.value === null || params.value === undefined) {
                  return "";
                }
                return params.value ? "Yes" : "No";
              },
            }
          : {
              valueFormatter: (params: { value: unknown }) => {
                const formatter =
                  cellFormatter ??
                  (c.type === "date"
                    ? ({ kind: "date" } as const)
                    : c.type === "timestamp"
                      ? ({ kind: "datetime" } as const)
                      : undefined);
                if (formatter) {
                  return formatExploreCellValue(params.value, formatter);
                }
                if (params.value === null || params.value === undefined) {
                  return "";
                }
                return String(params.value);
              },
            }),
        ...(c.type === "timestamp" || c.type === "date"
          ? {
              minWidth: c.field === defaultDateField ? 140 : 180,
            }
          : undefined),
      };
    });
}

export function buildReportColumnDefs(
  fields: readonly string[],
  columns: readonly EntityColumnDef[],
  labels: ReadonlyMap<string, string> = new Map(),
): ColDef[] {
  const columnMap = new Map(columns.map((c) => [c.field, c]));

  return fields.map((field) => {
    const meta = columnMap.get(field);
    const cellFormatter = meta?.formatter;
    return {
      field,
      headerName: labels.get(field) ?? meta?.label ?? field,
      sortable: true,
      resizable: true,
      minWidth: 140,
      ...(meta?.type === "boolean"
        ? {
            valueFormatter: (params: { value: unknown }) => {
              if (params.value === null || params.value === undefined) {
                return "";
              }
              return params.value ? "Yes" : "No";
            },
          }
        : {
            valueFormatter: (params: { value: unknown }) => {
              const formatter =
                cellFormatter ??
                (meta?.type === "date"
                  ? ({ kind: "date" } as const)
                  : meta?.type === "timestamp"
                    ? ({ kind: "datetime" } as const)
                    : meta?.type === "number"
                      ? ({ kind: "number" } as const)
                      : undefined);
              if (formatter) {
                return formatExploreCellValue(params.value, formatter);
              }
              if (params.value === null || params.value === undefined) {
                return "";
              }
              return String(params.value);
            },
          }),
    };
  });
}

function columnFilterType(type: string): string | boolean {
  switch (type) {
    case "number":
      return "agNumberColumnFilter";
    case "timestamp":
    case "date":
      return "agDateColumnFilter";
    case "enum":
      return "agSetColumnFilter";
    case "boolean":
      return "agSetColumnFilter";
    case "string":
      return "agTextColumnFilter";
    default:
      return true;
  }
}

function columnFilterParams(column: {
  type: string;
  enumValues?: readonly string[];
}) {
  if (column.type === "timestamp" || column.type === "date") {
    return {
      filterOptions: ["equals", "lessThan", "greaterThan", "inRange"],
    };
  }
  if (column.type === "enum" && column.enumValues) {
    return { values: [...column.enumValues] };
  }
  if (column.type === "boolean") {
    return { values: [true, false] };
  }
  return undefined;
}
